import React from 'react';
import PropTypes from 'prop-types';
import { dynastyTheme, withAlpha } from '../../../../../services/colorService';
import PlayerPhoto from './PlayerPhoto';
import EligibilityBadges from '../../../../EligibilityBadges';
import MobileInjuryBadge from '../../MobileInjuryBadge';
import { formatContractDisplay } from '../../../../../services/contractUtils';
import { formatHeight, calculateAge } from '../../../../../services/playerUtils';
import { fmtPrice } from './VitalStrip';
import { getOwnershipState, OWNERSHIP } from '../../../../../utils/playerOwnership';

const C = dynastyTheme.tokens.colors;

/**
 * PlayerHeader Component
 *
 * Line 1: Player name + jersey number
 * Line 2: Position · Team · Height · Weight · Age (compressed, gray, 11px)
 * Line 3: Price/Contract + Market Value + Ownership Badge + Streak Badge
 */
const getOwnershipBadge = (player) => {
  // Canonical ownership predicate (Bible §4 / DD-MODAL-0723, Rule 12 —
  // utils/playerOwnership is the ONE source). Pre-fix this hand-rolled
  // `!lc || status==='available' || !team_id` failed OPEN to a confident
  // "Free Agent" on missing league_context (Rule 9 violation class).
  const ownership = getOwnershipState(player);
  if (ownership.state === OWNERSHIP.UNKNOWN) {
    // Fail loud: absent/contradictory ownership data is a data problem,
    // never a "Free Agent" claim.
    return { label: 'Ownership unavailable', bg: withAlpha(C.error, 0.12), color: C.error, border: `0.5px solid ${withAlpha(C.error, 0.3)}` };
  }
  if (ownership.state === OWNERSHIP.FREE_AGENT) {
    return { label: 'Free Agent', bg: withAlpha(C.txn.add.stroke, 0.15), color: C.txn.add.stroke, border: `0.5px solid ${withAlpha(C.txn.add.stroke, 0.25)}` };
  }
  if (ownership.isUserTeam) {
    return { label: 'YOURS', bg: C.primaryTint.t15, color: C.primary, border: `0.5px solid ${withAlpha(C.goldBase, 0.25)}` };
  }
  return { label: ownership.teamName || 'Owned', bg: withAlpha(C.blue, 0.15), color: C.blue, border: `0.5px solid ${withAlpha(C.blue, 0.25)}` };
};

const PlayerHeader = ({
  player,
  showPhoto = true,
  showTrend = true,
  showContract = true,
  photoSize = '80x80',
  trendSize = 'standard',
  layout = 'horizontal'
}) => {
  const isHorizontal = layout === 'horizontal';
  const containerStyle = {
    display: 'flex',
    flexDirection: isHorizontal ? 'row' : 'column',
    alignItems: isHorizontal ? 'center' : 'flex-start',
    gap: isHorizontal ? '12px' : '8px',
    padding: '12px 16px',
    width: '100%',
    boxSizing: 'border-box',
  };

  if (!player) {
    return <div style={containerStyle}><div style={{ color: C.coolGray[500], padding: 10 }}>Loading player...</div></div>;
  }

  const mlb_id = player.ids?.mlb;
  const name = player.info?.full_name;
  const position = player.info?.position;
  const team = player.info?.mlb_team;
  const jersey_number = player.info?.jersey_number;
  const info = player.info || {};
  const financial = player.league_context?.financial || {};
  const hotCold = player.analytics?.hot_cold?.status;

  const ownerBadge = getOwnershipBadge(player);

  // Build compressed detail parts
  const ageVal = calculateAge(info.birthdate, { missingValue: null });
  const detailParts = [
    position,
    team,
    formatHeight(info.height_inches, { missingValue: null }),
    info.weight_pounds ? `${info.weight_pounds} lbs` : null,
    ageVal !== null ? `${ageVal} yrs` : null,
  ].filter(Boolean);

  // Price / contract
  const salary = financial.contract_salary;
  const contractType = financial.contract_type;
  const contractYears = financial.contract_years;
  const price = financial.baseline_price;
  const marketValue = financial.current_market_value;

  const contractText = (salary && salary > 0 && contractYears > 0)
    ? `${fmtPrice(salary)} · ${formatContractDisplay(contractType, contractYears)}`
    : (price != null ? `Price: ${fmtPrice(price)}` : null);

  return (
    <div style={containerStyle}>
      {showPhoto && (
        <div style={{ flexShrink: 0 }}>
          <PlayerPhoto mlbId={mlb_id} size={photoSize} />
        </div>
      )}

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {/* Line 1: Name + jersey */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontSize: 18, fontWeight: 700, color: C.white, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
          {jersey_number && <span style={{ fontSize: 14, fontWeight: 600, color: C.coolGray[550], flexShrink: 0 }}>#{jersey_number}</span>}
          <MobileInjuryBadge mlbId={mlb_id} style={{ fontSize: 9, alignSelf: 'center' }} />
        </div>

        {/* Line 2: Position · Team · Height · Weight · Age */}
        <div style={{ fontSize: 11, color: C.mutedLight, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {detailParts.join(' · ')}
        </div>

        {/* Line 2b: Eligibility badges */}
        <div style={{ marginTop: 1 }}>
          <EligibilityBadges player={player} size="sm" />
        </div>

        {/* Line 3: Contract/Price + MV + Ownership + Streak */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 1 }}>
          {contractText && <span style={{ fontSize: 10, fontWeight: 600, color: C.orange.base }}>{contractText}</span>}
          {marketValue > 0 && <span style={{ fontSize: 10, fontWeight: 600, color: C.green.bright }}>MV: {fmtPrice(marketValue)}</span>}

          {/* Ownership badge */}
          <span style={{
            fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 3,
            backgroundColor: ownerBadge.bg, color: ownerBadge.color, border: ownerBadge.border,
          }}>{ownerBadge.label}</span>

          {/* Streak badge */}
          {hotCold === 'hot' && <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 3, background: withAlpha(C.error, 0.15), color: C.spark.down }}>Hot</span>}
          {hotCold === 'cold' && <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 3, background: withAlpha(C.blue, 0.15), color: C.blue }}>Cold</span>}
        </div>
      </div>
    </div>
  );
};

PlayerHeader.propTypes = {
  player: PropTypes.object.isRequired,
  showPhoto: PropTypes.bool,
  showTrend: PropTypes.bool,
  showContract: PropTypes.bool,
  photoSize: PropTypes.string,
  trendSize: PropTypes.string,
  layout: PropTypes.oneOf(['horizontal', 'vertical'])
};

export default PlayerHeader;
